import React, { useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    Paper,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Card,
    CardContent,
    Link,
    Button,
    Avatar
} from '@mui/material';

import {
    ShoppingCartOutlined,
    GroupOutlined,
    SavingsOutlined,
    CheckCircleOutline,
    EmojiObjectsOutlined,
    KitchenOutlined,
    LocalLaundryServiceOutlined,
    StarBorderOutlined,
    HomeOutlined
} from '@mui/icons-material';

// スクロール用の関数
const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
        const offset = 80; // ヘッダーの高さ分のオフセット
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - offset;

        window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
        });
    }
};

const tocItems = [
    { id: 'introduction', label: 'ルームシェアと節約の魅力' },
    { id: 'daily-ideas', label: '今日からできる！節約アイデア集' },
    { id: 'group-buy', label: '共同購入で賢く節約するコツ' },
    { id: 'saving-goods', label: 'ルームシェアで役立つ節約グッズ' },
    { id: 'experiences', label: '体験談＆リアルな節約エピソード' },
    { id: 'conclusion', label: 'まとめとアドバイス' }
];

const PostContent = () => {
    const [showToc, setShowToc] = useState(true);
    return (
        <Box sx={{ maxWidth: 800, mx: 'auto', px: { xs: 1, sm: 3 } }}>
            {/* 目次 */}
            {showToc ? (
                <Card sx={{ mb: 4, boxShadow: 2, bgcolor: '#e3f2fd', border: '1px solid #ddd', borderRadius: 2 }}>
                    <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 700 }}>目次</Typography>
                            <Button variant="outlined" size="small" onClick={() => setShowToc(false)}>閉じる</Button>
                        </Box>
                        <List sx={{ p: 0 }}>
                            {tocItems.map((item, idx) => (
                                <ListItem key={item.id} sx={{ p: 0.5, display: 'flex', alignItems: 'center', minHeight: 36 }}>
                                    <Box sx={{
                                        minWidth: 32,
                                        minHeight: 32,
                                        bgcolor: '#f5f5f5',
                                        color: '#888',
                                        fontWeight: 'bold',
                                        fontSize: '1.1rem',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mr: 1
                                    }}>
                                        {idx + 1}.
                                    </Box>
                                    <Link
                                        component="button"
                                        onClick={() => scrollToSection(item.id)}
                                        color="inherit"
                                        underline="hover"
                                        sx={{ cursor: 'pointer', fontSize: '0.97rem', textAlign: 'left', lineHeight: 1.3 }}
                                    >
                                        {item.label}
                                    </Link>
                                </ListItem>
                            ))}
                        </List>
                    </CardContent>
                </Card>
            ) : (
                <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setShowToc(true)}
                    sx={{ mb: 2 }}
                >
                    目次を表示
                </Button>
            )}

            {/* 導入 */}
            <Box id="introduction" sx={{ mb: 5 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアと節約の魅力
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    物価高騰、家賃の高止まり、光熱費や食費の負担増——。そんな時代でも、賢く「楽しく」節約できるのがルームシェアの最大の魅力です。家賃や生活費を抑えるだけでなく、ルームメイト同士で知恵を出し合って、日々をより豊かに、効率的に暮らすことができます。
                </Typography>
                <Typography variant="body1" sx={{ mb: 3 }}>
                    この記事では、実際の体験やリアルな節約ノウハウをもとに、今日から実践できる「ルームシェアのおすすめ節約術」を総まとめ。共同購入のコツや、便利な節約グッズ、さらにリアルなエピソードまで、役立つ情報をたっぷりご紹介します！
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <img
                        src="/images/blog/share-living-meal_roomshare-osusume-setsuyaku.jpg"
                        alt="明るい共有リビングで、3人のルームメイトたちが手作りの食事を囲みながらリラックスした雰囲気で会話を楽しんでいる様子。温かみのある木製テーブルと、カラフルな食器、自然光がたっぷりと差し込む開放感のある空間が印象的。"
                        style={{
                            width: '100%',
                            maxWidth: 520,
                            borderRadius: 12,
                            boxShadow: '0 2px 16px rgba(0,0,0,0.11)',
                            marginBottom: 0
                        }}
                    />
                </Box>
            </Box>

            {/* 節約アイデア集 */}
            <Box id="daily-ideas" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    今日からできる！節約アイデア集
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアでは、ちょっとの工夫で毎月の出費がグッと減らせます。すぐに実践できる節約アイデアをピックアップしました！
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <SavingsOutlined sx={{ color: '#1976d2', mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.12rem' }}>
                                    光熱費の徹底管理
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                                ・エアコンや照明は「つけっぱなし禁止」をルール化
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                                ・節電タップやLED電球でコスト削減
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#444' }}>
                                ・浴槽はまとめて使い、追い焚き回数を減らす
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <KitchenOutlined sx={{ color: '#388e3c', mr: 1 }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.12rem' }}>
                                    共同自炊で食費節約
                                </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                                ・週2〜3回、みんなでまとめて料理
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#444', mb: 0.5 }}>
                                ・食材の無駄を防ぎ、コスパもアップ
                            </Typography>
                            <Typography variant="body2" sx={{ color: '#444' }}>
                                ・冷蔵庫の中身をシェアして把握
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#1976d2' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="日用品はまとめ買い＆シェアで大幅コストダウン"
                            secondary="トイレットペーパーや洗剤など、みんなで使うものは大容量パックがお得。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#1976d2' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="宅配便や通販の送料はシェアして節約"
                            secondary="みんなで一緒に注文して送料無料ラインを活用しよう。"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#1976d2' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="ゴミ袋や資源回収も効率化"
                            secondary="分別を徹底し、余計なごみ袋代や罰金を未然に防ぐ。"
                        />
                    </ListItem>
                </List>
                <Box sx={{ mt: 2, mb: 1 }}>
                    <Chip icon={<EmojiObjectsOutlined />} label="POINT" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Typography variant="body2" display="inline">
                        ルールや役割分担を明確にすることで、トラブルや無駄な出費を防げます。
                    </Typography>
                </Box>
            </Box>

            {/* 共同購入で賢く節約 */}
            <Box id="group-buy" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    共同購入で賢く節約するコツ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェア最大のメリットのひとつが「共同購入」。みんなで協力して買うことで、質の良いものを安く手に入れられます。そのコツをご紹介します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <ShoppingCartOutlined sx={{ color: '#d84315', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    共同購入のコツ
                                </Typography>
                            </Box>
                            <List dense>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <StarBorderOutlined sx={{ color: '#d84315' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="予算・必要量を事前に相談"
                                        secondary="人数・消費ペースを考慮して無駄買いを防止"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <StarBorderOutlined sx={{ color: '#d84315' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="お金の管理はアプリや共有ノートで明確に"
                                        secondary="支払い・精算を“見える化”してトラブル防止"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemIcon>
                                        <StarBorderOutlined sx={{ color: '#d84315' }} />
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="スーパーの特売日やネットまとめ買いを活用"
                                        secondary="大型店舗や通販のポイントを賢くシェア"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={0} sx={{ p: 2, bgcolor: '#fafafa', borderRadius: 2, height: '100%' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <GroupOutlined sx={{ color: '#00838f', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    便利なアプリ例
                                </Typography>
                            </Box>
                            <List dense>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemText
                                        primary="LINEグループ・LINE Pay"
                                        secondary="チャットで相談〜割り勘・送金まで一括管理"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemText
                                        primary="家計簿アプリ（Zaim、MoneyForwardなど）"
                                        secondary="支出の共有や履歴が分かりやすい"
                                    />
                                </ListItem>
                                <ListItem sx={{ pl: 0 }}>
                                    <ListItemText
                                        primary="Googleスプレッドシート"
                                        secondary="みんなで予算や在庫管理が可能"
                                    />
                                </ListItem>
                            </List>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Chip icon={<GroupOutlined />} label="TIP" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Typography variant="body2" display="inline">
                        定期的な「共有品リスト」の見直しで、無駄な買い足しを減らしましょう。
                    </Typography>
                </Box>
            </Box>

            {/* 節約グッズ紹介 */}
            <Box id="saving-goods" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    ルームシェアで役立つ節約グッズ
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ちょっとしたアイテムを導入するだけで、節約効率がさらにアップ！「これは便利」と評判のグッズを厳選してご紹介します。
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345, bgcolor: '#fafafa', borderRadius: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <LocalLaundryServiceOutlined sx={{ color: '#1565c0', mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        洗濯ボール
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    洗剤の量を減らしつつ、汚れもスッキリ。共同洗濯時のコスト削減に。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345, bgcolor: '#fafafa', borderRadius: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <SavingsOutlined sx={{ color: '#c62828', mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        節水シャワーヘッド
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    毎日の水道代を大幅カット。取り付けも簡単で、シェアハウスでも人気。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <Card sx={{ maxWidth: 345, bgcolor: '#fafafa', borderRadius: 2 }}>
                            <CardContent>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                    <EmojiObjectsOutlined sx={{ color: '#ffd600', mr: 1 }} />
                                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                        LED電球
                                    </Typography>
                                </Box>
                                <Typography variant="body2" sx={{ mb: 1 }}>
                                    消費電力が少なく、長寿命。リビングや廊下の電気代も節約できます。
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                    <img
                        src="/images/blog/eco-goods_roomshare-osusume-setsuyaku.jpg"
                        alt="キッチンカウンターに洗濯ボールや節水シャワーヘッド、LED電球などの節約グッズが並び、ルームシェア生活をより効率的で快適にするためのアイテム紹介のイメージ。明るい照明と白基調の清潔感ある雰囲気。"
                        style={{
                            width: '100%',
                            maxWidth: 420,
                            borderRadius: 12,
                            boxShadow: '0 2px 16px rgba(0,0,0,0.11)'
                        }}
                    />
                </Box>
                <List dense>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#1976d2' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="電源タップ（節電スイッチ付き）"
                            secondary="待機電力カットで毎月の電気代を確実に減らす"
                        />
                    </ListItem>
                    <ListItem>
                        <ListItemIcon>
                            <CheckCircleOutline sx={{ color: '#1976d2' }} />
                        </ListItemIcon>
                        <ListItemText
                            primary="分別ゴミ箱・ラベルシール"
                            secondary="ごみ出しルールの徹底で余計なコストや手間もダウン"
                        />
                    </ListItem>
                </List>
            </Box>

            {/* 体験談＆リアルな節約エピソード */}
            <Box id="experiences" sx={{ mb: 6 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    体験談＆リアルな節約エピソード
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    実際にルームシェアで節約を実践している人たちの声をご紹介します。リアルな工夫や、思わぬ失敗談もぜひ参考に！
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/user1.jpg" sx={{ width: 36, height: 36, mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Yさん（20代女性・会社員）</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#444' }}>
                                「みんなで炊飯器のサイズを大きいものに買い替え、まとめてご飯を炊き冷凍保存。食費と電気代が月1000円以上節約できました！」
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/user2.jpg" sx={{ width: 36, height: 36, mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Nさん（30代男性・IT系）</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#444' }}>
                                「日用品の共同購入で、毎月の細かい出費が激減。特売情報やクーポンをグループLINEで共有するのが習慣になりました。」
                            </Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12}>
                        <Paper elevation={1} sx={{ p: 2, borderRadius: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <Avatar src="/images/avatars/user3.jpg" sx={{ width: 36, height: 36, mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Kさん（20代男性・学生）</Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: '#444' }}>
                                「役割分担を決めずにいた時期は、洗剤や調味料がダブって無駄が発生。『今月の購入担当』制度を取り入れてからは無駄買い＆トラブルがゼロに！」
                            </Typography>
                        </Paper>
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                    <img
                        src="/images/blog/roommate-meeting_roomshare-osusume-setsuyaku.jpg"
                        alt="明るいダイニングテーブルを囲み、4人のルームメイトが家計や節約について話し合っている様子。ノートPCや紙の家計簿が広げられ、協力し合う姿勢や和やかな雰囲気が伝わる写真。"
                        style={{
                            width: '100%',
                            maxWidth: 500,
                            borderRadius: 12,
                            boxShadow: '0 2px 16px rgba(0,0,0,0.10)'
                        }}
                    />
                </Box>
                <Box sx={{ mt: 2 }}>
                    <Chip icon={<HomeOutlined />} label="EXPERT ADVICE" color="primary" variant="outlined" sx={{ mr: 1 }} />
                    <Typography variant="body2" display="inline">
                        節約ルールをみんなで話し合い、定期的に見直すことが長続きのコツです。
                    </Typography>
                </Box>
            </Box>

            {/* まとめ・アドバイス */}
            <Box id="conclusion" sx={{ mb: 8 }}>
                <Typography variant="h4" component="h2" gutterBottom>
                    まとめとアドバイス
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Typography variant="body1" sx={{ mb: 2 }}>
                    ルームシェアは、ただ家賃を抑えるだけでなく、知恵と工夫、そしてコミュニケーションで暮らしがどんどん豊かになります。節約のためには「みんなでルールを作る」「役割分担を明確にする」「便利グッズやアプリを活用する」ことが大切です。
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    それぞれのライフスタイルに合った方法を選び、楽しみながら節約を続けていきましょう。この記事が、あなたのルームシェア生活をより快適でお得にするヒントになれば幸いです！
                </Typography>
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                    <Chip label="ルームシェア" color="primary" variant="outlined" sx={{ mx: 1, my: 0.5 }} />
                    <Chip label="節約術" color="primary" variant="outlined" sx={{ mx: 1, my: 0.5 }} />
                    <Chip label="共同生活" color="primary" variant="outlined" sx={{ mx: 1, my: 0.5 }} />
                </Box>
            </Box>
        </Box>
    );
};

const post = {
    id: 'roomshare-osusume-setsuyaku',
    slug: 'roomshare-osusume-setsuyaku',
    title: 'ルームシェアの「おすすめ節約術」まとめ',
    excerpt: '節約アイデア 共同購入のコツ 節約グッズ紹介 体験談',
    coverImage: '/images/blog/cover-roomshare-saving_roomshare-osusume-setsuyaku.jpg',
    coverImageAlt: '明るい自然光が差し込むモダンなルームシェアの共有リビングで、多様な背景を持つルームメイトたちが楽しく会話をしている様子を通じて、共同生活の温かさと節約の知恵が伝わるイメージ',
    publishedAt: '2024-06-16T00:00:00.000Z',
    isPublished: true,
    categories: ['ルームシェア', '節約', 'ライフハック'],
    tags: ['ルームシェア', '節約術', '共同購入', '生活術'],
    author: {
        name: '三宅 真理',
        avatar: '/images/avatars/miyake-mari.jpg',
        bio: 'ルームシェア歴10年。都内・関西での多拠点シェア経験を活かし、実践的な節約術や快適な共同生活ノウハウを発信中。住みやすく、心地よいシェアハウス作りがモットー。',
        profilePicture: '/images/avatars/miyake-mari.jpg'
    },
    viewCount: 1320,
    contentComponent: PostContent
};

export default post;